import { defineStore } from 'pinia';
import {
  contentDb,
  type PageDetailRow,
  type PageItem,
  isDetailRowFullyFresh,
} from '@/db/content';
import {
  markContractGenerationApplied,
  needsContractListingRefresh,
  shouldSendHttpCacheValidators,
} from '@/db/contentCache';
import { DEFAULT_LOCALE } from '@/i18n';
import {
  getTranslationLinks as getTranslationLinksFromNav,
  getRootPathsAndDefaultLocale,
  getUrlForItem,
  expandListingItemsWithLocaleVariants,
  indexPageItemsByTranslationKey,
  resolvePageFromPathname,
  type ResolvedFooterLinkColumn,
  type TopNavData,
  type TranslationLink,
} from '@/navTree';
import { PAGES_URL, devFetchDelay, fetchPageDetailFromApi, fetchPagePreview } from './content/api';
import { applyListingResponse, getListingFromPageTable } from './content/cache';
import {
  extractDetailSliceFromConsolidated,
  findConsolidatedRowByPageId,
} from './content/consolidatedIdb';
import { parsePreviewQuery } from './content/routeSync';
import type {
  ContentStatus,
  PageDetailState,
  PagesApiResponse,
  RouteQueryLike,
  SiteBootstrapErrorKind,
} from './content/types';
import { buildNormalPageContentSidebar } from '@/nav/normalPageSidebar';

// Re-export nav tree types for consumers that import from the store
export type { NavPage } from '@/navTree';

type ListingRefreshOutcome = 'notModified' | 'updated';
type TopMenuPageEntry = NonNullable<TopNavData['topMenu']>['pages'][number];
type ResolvedMenuPageEntry = {
  id: number;
  page_id?: number;
  title: string;
  url: string;
  translation_key?: string;
  icon?: string;
};
type TopMenuChildLink = {
  id: number;
  title: string;
  url: string;
  translation_key?: string;
};
type TopMenuLinkWithChildren = ResolvedMenuPageEntry & { children: TopMenuChildLink[] };
export type ResolvedNavigationModel = {
  topNavData: TopNavData | null;
  topMenuLinks: TopMenuLinkWithChildren[];
  footerLinkColumns: ResolvedFooterLinkColumn[];
  footerLegalLinks: { title: string; url: string }[];
};

const MATERIALIZED_PATH_SECTION_LENGTH = 4;

/** Minimal detail-shaped payload from listing only (offline shell before full detail exists). */
function pageDetailFromListingItem(item: PageItem): PageDetailRow {
  return {
    id: item.id,
    title: item.title,
    meta: item.meta,
    detailDate: item.meta.last_published_at ?? null,
    rich_title: item.rich_title,
    header_style: item.header_style,
  };
}

/** Listing from API / IDB stays canonical (default locale rows + locale_variants). */
function normalizePagesApiResponse(data: PagesApiResponse): PagesApiResponse {
  return {
    ...data,
    items: [...data.items],
  };
}

/** Flatten canonical rows for pathname resolution, per-locale trees, and nav indices. */
function listingItemsExpandedForNav(raw: PageItem[] | undefined): PageItem[] {
  if (!raw?.length) return [];
  return expandListingItemsWithLocaleVariants(raw);
}

function getParentPath(path: string | undefined): string | null {
  if (!path || path.length <= MATERIALIZED_PATH_SECTION_LENGTH) return null;
  return path.slice(0, -MATERIALIZED_PATH_SECTION_LENGTH);
}

function getNavigationForLocale(
  data: PagesApiResponse | null,
  locale: string
): TopNavData | null {
  if (!data?.navigation) return null;
  const direct = normalizeTopNavData(data.navigation[locale]);
  if (direct) return direct;
  return normalizeTopNavData(data.navigation[DEFAULT_LOCALE]);
}

function getPreviewNavigation(payload: PageDetailRow | null | undefined): TopNavData | null {
  const navigation = (payload as { navigation?: unknown } | undefined)?.navigation;
  return normalizeTopNavData(navigation);
}

function getRootItemForLocale(items: PageItem[], locale: string): PageItem | null {
  const localeItems = items.filter((item) => item.meta.locale === locale);
  if (!localeItems.length) return null;
  return localeItems.reduce((root, item) => {
    if (item.meta.path.length !== root.meta.path.length) {
      return item.meta.path.length < root.meta.path.length ? item : root;
    }
    return item.meta.path.localeCompare(root.meta.path) < 0 ? item : root;
  });
}

function getRootItemFromNavigation(
  items: PageItem[],
  locale: string,
  nav: TopNavData | null
): PageItem | null {
  if (!items.length) return null;
  const navRootId = nav?.siteRootId;
  if (navRootId != null) {
    return (
      items.find((item) => item.id === navRootId && item.meta.locale === locale) ??
      items.find((item) => item.id === navRootId) ??
      getRootItemForLocale(items, locale)
    );
  }
  return getRootItemForLocale(items, locale);
}

function withResolvedSiteRootFields(
  nav: TopNavData | null,
  items: PageItem[],
  locale: string
): TopNavData | null {
  if (!nav) return null;
  const rootItem = getRootItemFromNavigation(items, locale, nav);
  if (!rootItem) return nav;
  const { rootPathByLocale, defaultLocale } = getRootPathsAndDefaultLocale(items);
  return {
    ...nav,
    siteRootId: rootItem.id,
    siteRootTitle: rootItem.title,
    siteRootUrl: getUrlForItem(rootItem, rootPathByLocale, defaultLocale),
    siteRootTranslationKey: rootItem.meta.translation_key,
  };
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeMenuEntry(raw: unknown): TopMenuPageEntry | null {
  if (!isObjectRecord(raw)) return null;
  const id = typeof raw.id === 'number' ? raw.id : undefined;
  const pageId = typeof raw.page_id === 'number' ? raw.page_id : undefined;
  const title = typeof raw.title === 'string' ? raw.title : undefined;
  const url = typeof raw.url === 'string' ? raw.url : undefined;
  const translationKey =
    typeof raw.translation_key === 'string' ? raw.translation_key : undefined;
  const icon = typeof raw.icon === 'string' ? raw.icon : undefined;
  if (id == null && pageId == null && !title && !url && !translationKey) return null;
  return {
    id,
    page_id: pageId,
    title,
    url,
    translation_key: translationKey,
    icon,
  };
}

function normalizeTopNavData(value: unknown): TopNavData | null {
  if (!isObjectRecord(value)) return null;
  const topMenuPagesRaw = isObjectRecord(value.topMenu) && Array.isArray(value.topMenu.pages)
    ? value.topMenu.pages
    : [];
  const topMenuPages = topMenuPagesRaw
    .map((entry) => normalizeMenuEntry(entry))
    .filter((entry): entry is TopMenuPageEntry => entry !== null);
  return {
    siteRootTitle: typeof value.siteRootTitle === 'string' ? value.siteRootTitle : '',
    siteRootUrl: typeof value.siteRootUrl === 'string' ? value.siteRootUrl : '/',
    siteRootId: typeof value.siteRootId === 'number' ? value.siteRootId : undefined,
    siteRootTranslationKey:
      typeof value.siteRootTranslationKey === 'string'
        ? value.siteRootTranslationKey
        : undefined,
    siteName: typeof value.siteName === 'string' ? value.siteName : undefined,
    siteSubtitle: typeof value.siteSubtitle === 'string' ? value.siteSubtitle : undefined,
    siteLogos: Array.isArray(value.siteLogos) ? value.siteLogos : undefined,
    footerLogo:
      'footerLogo' in value
        ? value.footerLogo === null
          ? null
          : typeof value.footerLogo === 'object' && value.footerLogo !== null
            ? (value.footerLogo as NonNullable<TopNavData['footerLogo']>)
            : undefined
        : undefined,
    footerLogoText:
      typeof value.footerLogoText === 'string' ? value.footerLogoText : undefined,
    socialLinks: Array.isArray(value.socialLinks) ? value.socialLinks : undefined,
    copyrightText:
      typeof value.copyrightText === 'string' ? value.copyrightText : undefined,
    footerContactAddress:
      typeof value.footerContactAddress === 'string'
        ? value.footerContactAddress
        : undefined,
    footerContactPhone:
      typeof value.footerContactPhone === 'string' ? value.footerContactPhone : undefined,
    footerContactEmail:
      typeof value.footerContactEmail === 'string' ? value.footerContactEmail : undefined,
    topMenu: topMenuPages.length ? { pages: topMenuPages } : undefined,
    footerLinkColumns: normalizeFooterLinkColumns(value.footerLinkColumns),
    footerLegalLinks: normalizeFooterLegalLinks(value.footerLegalLinks),
    rootPages: Array.isArray(value.rootPages) ? (value.rootPages as TopNavData['rootPages']) : [],
  };
}

function normalizeFooterLegalLinks(
  raw: unknown
): TopNavData['footerLegalLinks'] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: NonNullable<TopNavData['footerLegalLinks']> = [];
  for (const link of raw) {
    if (!isObjectRecord(link)) continue;
    const pid = typeof link.page_id === 'number' ? link.page_id : undefined;
    if (pid != null) out.push({ page_id: pid });
  }
  return out.length ? out : undefined;
}

function normalizeFooterLinkColumns(
  raw: unknown
): TopNavData['footerLinkColumns'] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: NonNullable<TopNavData['footerLinkColumns']> = [];
  for (const col of raw) {
    if (!isObjectRecord(col)) continue;
    const title = typeof col.title === 'string' ? col.title.trim() : '';
    const linksRaw = Array.isArray(col.links) ? col.links : [];
    const links: { page_id: number }[] = [];
    for (const link of linksRaw) {
      if (!isObjectRecord(link)) continue;
      const pid = typeof link.page_id === 'number' ? link.page_id : undefined;
      if (pid != null) links.push({ page_id: pid });
    }
    if (title && links.length) {
      out.push({ title, links });
    }
  }
  return out.length ? out : undefined;
}

function buildNavigationIndices(items: PageItem[]) {
  const { rootPathByLocale, defaultLocale } = getRootPathsAndDefaultLocale(items);
  const itemById = new Map(items.map((item) => [item.id, item]));
  const itemsByTranslationKey = indexPageItemsByTranslationKey(items);
  return { rootPathByLocale, defaultLocale, itemById, itemsByTranslationKey };
}

function buildFooterLinkColumns(
  columns: TopNavData['footerLinkColumns'] | undefined,
  items: PageItem[],
  locale: string
): ResolvedFooterLinkColumn[] {
  if (!columns?.length || !items.length) return [];
  const { rootPathByLocale, defaultLocale, itemById, itemsByTranslationKey } =
    buildNavigationIndices(items);
  const out: ResolvedFooterLinkColumn[] = [];
  for (const col of columns) {
    const title = col.title?.trim() ?? '';
    if (!title) continue;
    const itemsOut: { title: string; url: string }[] = [];
    for (const link of col.links ?? []) {
      const pageId = link.page_id;
      if (pageId == null) continue;
      const byId = itemById.get(pageId);
      if (!byId) continue;
      const pageTranslationKey = byId.meta.translation_key;
      const localized = pageTranslationKey
        ? itemsByTranslationKey.get(pageTranslationKey)?.get(locale)
        : undefined;
      const displayItem = localized ?? byId;
      const pt = displayItem.title?.trim();
      if (!pt) continue;
      itemsOut.push({
        title: pt,
        url: getUrlForItem(displayItem, rootPathByLocale, defaultLocale),
      });
    }
    if (itemsOut.length) {
      out.push({ title, items: itemsOut });
    }
  }
  return out;
}

function buildFooterLegalLinks(
  links: TopNavData['footerLegalLinks'] | undefined,
  items: PageItem[],
  locale: string
): { title: string; url: string }[] {
  if (!links?.length || !items.length) return [];
  const { rootPathByLocale, defaultLocale, itemById, itemsByTranslationKey } =
    buildNavigationIndices(items);
  const out: { title: string; url: string }[] = [];
  for (const link of links) {
    const pageId = link.page_id;
    if (pageId == null) continue;
    const byId = itemById.get(pageId);
    if (!byId) continue;
    const pageTranslationKey = byId.meta.translation_key;
    const localized = pageTranslationKey
      ? itemsByTranslationKey.get(pageTranslationKey)?.get(locale)
      : undefined;
    const displayItem = localized ?? byId;
    const pt = displayItem.title?.trim();
    if (!pt) continue;
    out.push({
      title: pt,
      url: getUrlForItem(displayItem, rootPathByLocale, defaultLocale),
    });
  }
  return out;
}

function buildTopMenuLinksWithChildren(
  items: PageItem[],
  locale: string,
  topMenuPages: TopMenuPageEntry[] = []
): TopMenuLinkWithChildren[] {
  if (!topMenuPages.length) return [];
  if (!items.length) {
    return topMenuPages.map((page, index) => ({
      id: page.id ?? page.page_id ?? index + 1,
      page_id: page.page_id,
      title: page.title ?? '',
      url: page.url ?? '/',
      translation_key: page.translation_key,
      icon: page.icon,
      children: [],
    }));
  }

  const { rootPathByLocale, defaultLocale, itemById, itemsByTranslationKey } =
    buildNavigationIndices(items);

  const collectChildrenForParent = (
    parent: PageItem,
    childLocale: string
  ): TopMenuChildLink[] =>
    items
      .filter((item) => {
        if (item.meta.locale !== childLocale) return false;
        return getParentPath(item.meta.path) === parent.meta.path;
      })
      .sort((a, b) => a.meta.path.localeCompare(b.meta.path))
      .map((item) => {
        const localized = item.meta.translation_key
          ? itemsByTranslationKey.get(item.meta.translation_key)?.get(locale)
          : undefined;
        const displayItem = localized ?? item;
        return {
          id: displayItem.id,
          title: displayItem.title,
          url: getUrlForItem(displayItem, rootPathByLocale, defaultLocale),
          translation_key: item.meta.translation_key,
        };
      });

  return topMenuPages.map((page) => {
    const pageId = page.id ?? page.page_id;
    const byId = pageId != null ? itemById.get(pageId) : undefined;
    const pageTranslationKey = page.translation_key ?? byId?.meta.translation_key;
    const byTranslationKey = pageTranslationKey
      ? itemsByTranslationKey.get(pageTranslationKey)?.get(locale)
      : undefined;
    const parentForLocale =
      (byId && byId.meta.locale === locale ? byId : undefined) ?? byTranslationKey;

    const parentTetum =
      (byId && byId.meta.locale === DEFAULT_LOCALE ? byId : undefined) ??
      (pageTranslationKey
        ? itemsByTranslationKey.get(pageTranslationKey)?.get(DEFAULT_LOCALE)
        : undefined);
    const displayItem = parentForLocale ?? parentTetum ?? byId;
    const resolvedId = pageId ?? displayItem?.id ?? 0;
    const resolvedTitle =
      displayItem?.title?.trim() || page.title?.trim() || '';
    const resolvedUrl = displayItem
      ? getUrlForItem(displayItem, rootPathByLocale, defaultLocale)
      : page.url?.trim() || '/';
    const resolvedIcon = displayItem?.meta.icon ?? page.icon ?? undefined;

    const parentForType = parentTetum ?? parentForLocale ?? byId;
    const rawChildren = parentTetum
      ? collectChildrenForParent(parentTetum, DEFAULT_LOCALE)
      : parentForLocale
        ? collectChildrenForParent(parentForLocale, locale)
        : [];
    const children =
      parentForType?.meta.type === 'home.NewsIndexPage' ? [] : rawChildren;

    return {
      id: resolvedId,
      page_id: page.page_id,
      title: resolvedTitle,
      url: resolvedUrl,
      icon: resolvedIcon ?? undefined,
      translation_key: pageTranslationKey,
      children,
    };
  });
}

function getMergedNavigationForLocale(
  data: PagesApiResponse | null,
  previewPayload: PageDetailRow | null | undefined,
  previewActive: boolean,
  locale: string
): TopNavData | null {
  const published = getNavigationForLocale(data, locale);
  if (!previewActive) return published;
  const previewType = (previewPayload?.meta as { type?: string } | undefined)?.type;
  if (previewType !== 'home.HomePage' || !previewPayload) return published;
  const previewNavigation = getPreviewNavigation(previewPayload);
  if (!previewNavigation) return published;
  return { ...published, ...previewNavigation };
}


export const useContentStore = defineStore('content', {
  state: () => ({
    status: 'uninitialized' as ContentStatus,
    error: null as string | null,
    /** Set when the site listing/root cannot bootstrap the public home (distinct from generic API errors). */
    siteBootstrapError: null as SiteBootstrapErrorKind | null,
    data: null as PagesApiResponse | null,
    /** Page detail payloads by id (when fetched or known missing). */
    pageDetailsById: {} as Record<number, PageDetailRow | null>,
    /** Explicit typed-detail state by page id. */
    detailStateById: {} as Record<number, PageDetailState>,
    /** Page ids currently being fetched (for loading state). */
    loadingDetailIds: {} as Record<number, true>,
    /** Current route/page orchestration state for view components. */
    currentPageId: null as number | null,
    currentPageLoading: false as boolean,
    currentPageError: null as string | null,
    /** True when offline: route matches listing but full page detail is unavailable (§8 shell). */
    currentPageShellOnly: false as boolean,
    currentPageNotFound: false as boolean,
    currentPreview: {
      isActive: false,
      contentType: null as string | null,
      token: null as string | null,
      loading: false,
      error: false,
      payload: undefined as PageDetailRow | null | undefined,
    },
    currentRouteRequestKey: 0 as number,
  }),

  getters: {
    /** Canonical rows plus synthetic rows from locale_variants (nav, search, route match). */
    expandedListingItems: (state): PageItem[] =>
      listingItemsExpandedForNav(state.data?.items),
    getTopNavDataForLocale:
      (state) =>
      (locale: string): TopNavData | null => {
        const nav = getNavigationForLocale(state.data, locale);
        const items = listingItemsExpandedForNav(state.data?.items);
        return withResolvedSiteRootFields(nav, items, locale);
      },
    getPageItemsForLocale:
      (state) =>
      (locale: string): PageItem[] => {
        const items = listingItemsExpandedForNav(state.data?.items);
        return items.filter((item) => item.meta.locale === locale);
      },
    getChildrenForPageId:
      (state) =>
      (parentId: number, locale: string): PageItem[] => {
        const items = listingItemsExpandedForNav(state.data?.items);
        if (!items.length) return [];
        const parent = items.find((item) => item.id === parentId && item.meta.locale === locale);
        if (!parent) return [];
        const d = items
          .filter((item) => {
            if (item.meta.locale !== locale) return false;
            return getParentPath(item.meta.path) === parent.meta.path;
          })
        return d.sort((a, b) => a.meta.path.localeCompare(b.meta.path));
      },
    getChildLinksForPageId:
      (state) =>
      (parentId: number, locale: string): TopMenuChildLink[] => {
        const items = listingItemsExpandedForNav(state.data?.items);
        if (!items.length) return [];
        const parent = items.find((item) => item.id === parentId && item.meta.locale === locale);
        if (!parent) return [];
        const { rootPathByLocale, defaultLocale } = getRootPathsAndDefaultLocale(items);
        const d = items
          .filter((item) => {
            if (item.meta.locale !== locale) return false;
            return getParentPath(item.meta.path) === parent.meta.path;
          })
        return d
          .sort((a, b) => a.meta.path.localeCompare(b.meta.path))
          .map((item) => ({
            id: item.id,
            title: item.title,
            url: getUrlForItem(item, rootPathByLocale, defaultLocale),
            translation_key: item.meta.translation_key,
          }));
      },
    getTopMenuLinksWithChildren:
      (state) =>
      (locale: string, topMenuPages: TopMenuPageEntry[] = []): TopMenuLinkWithChildren[] => {
        const items = listingItemsExpandedForNav(state.data?.items);
        return buildTopMenuLinksWithChildren(items, locale, topMenuPages);
      },
    getResolvedNavigation:
      (state) =>
      (locale: string): ResolvedNavigationModel => {
        const previewPayload = state.currentPreview.payload;
        const previewActive = state.currentPreview.isActive;
        const topNavData = getMergedNavigationForLocale(
          state.data,
          previewPayload,
          previewActive,
          locale
        );
        const items = listingItemsExpandedForNav(state.data?.items);
        const resolvedTopNavData = withResolvedSiteRootFields(topNavData, items, locale);
        // Top bar order and membership follow Tetum HomePage only; titles/URLs still resolve per UI locale in buildTopMenuLinksWithChildren. When previewing a non-Tetum HomePage, merged Tetum nav (published + any preview overlay) still drives this list.
        const mergedForTet = getMergedNavigationForLocale(
          state.data,
          previewPayload,
          previewActive,
          DEFAULT_LOCALE
        );
        const topMenuPages =
          mergedForTet?.topMenu?.pages ?? resolvedTopNavData?.topMenu?.pages ?? [];
        return {
          topNavData: resolvedTopNavData,
          topMenuLinks: buildTopMenuLinksWithChildren(items, locale, topMenuPages),
          footerLinkColumns: buildFooterLinkColumns(
            resolvedTopNavData?.footerLinkColumns,
            items,
            locale
          ),
          footerLegalLinks: buildFooterLegalLinks(
            resolvedTopNavData?.footerLegalLinks,
            items,
            locale
          ),
        };
      },
    getSideNavDataForLocale:
      (state) =>
      (locale: string): TopNavData | null => {
        const merged = getMergedNavigationForLocale(
          state.data,
          state.currentPreview.payload,
          state.currentPreview.isActive,
          locale
        );
        const items = listingItemsExpandedForNav(state.data?.items);
        return withResolvedSiteRootFields(merged, items, locale);
      },
    /** Section sidebar for NormalPage + header_style content (listing + site root). */
    getNormalPageContentSidebar:
      (state) =>
      (locale: string, pageId: number) => {
        const merged = getMergedNavigationForLocale(
          state.data,
          state.currentPreview.payload,
          state.currentPreview.isActive,
          locale
        );
        const items = listingItemsExpandedForNav(state.data?.items);
        const resolved = withResolvedSiteRootFields(merged, items, locale);
        return buildNormalPageContentSidebar(locale, pageId, items, resolved?.siteRootId);
      },
    /** Page from the listing by id (for resolving title/URL in blocks). */
    getPageById:
      (state) =>
      (id: number): PageItem | null => {
        const items = listingItemsExpandedForNav(state.data?.items);
        return items.find((item) => item.id === id) ?? null;
      },
    /** Locale HomePage id from the pages listing, or null when listing is unavailable. */
    getHomePageIdForLocale:
      (state) =>
      (locale: string): number | null => {
        const items = state.data?.items;
        if (!items?.length) return null;
        const homePage = items.find((item) => {
          const meta = item.meta as { type?: string; locale?: string };
          return meta?.type === 'home.HomePage' && meta?.locale === locale;
        });
        return homePage?.id ?? null;
      },
    /** Build frontend URL for a page id using the current listing. */
    getUrlForPageId:
      (state) =>
      (pageId: number): string => {
        const items = listingItemsExpandedForNav(state.data?.items);
        const item = items.find((i) => i.id === pageId);
        if (!item) return '/';
        const { rootPathByLocale, defaultLocale } = getRootPathsAndDefaultLocale(items);
        return getUrlForItem(item, rootPathByLocale, defaultLocale);
      },
    /** Links to the same page in other languages for the language switcher. */
    getTranslationLinks:
      (state) =>
      (pathname: string): TranslationLink[] =>
        state.data
          ? getTranslationLinksFromNav(listingItemsExpandedForNav(state.data.items), pathname)
          : [],
    getPageDetailState:
      (state) =>
      (id: number): PageDetailState =>
        state.detailStateById[id] ?? 'idle',
    isTypedDetailReady:
      (state) =>
      (id: number): boolean =>
        state.detailStateById[id] === 'ready',
    hasTypedDetail:
      (state) =>
      (id: number): boolean =>
        state.detailStateById[id] === 'ready' && state.pageDetailsById[id] != null,
    getCurrentPagePayload(state): PageDetailRow | null | undefined {
      if (state.currentPreview.isActive) {
        return state.currentPreview.payload;
      }
      if (state.currentPageId == null) return undefined;
      if (state.currentPageShellOnly && state.data?.items?.length) {
        const items = listingItemsExpandedForNav(state.data.items);
        const item = items.find((i) => i.id === state.currentPageId);
        if (item) {
          return pageDetailFromListingItem(item);
        }
      }
      return state.pageDetailsById[state.currentPageId];
    },
    isCurrentPageShellOnly: (state): boolean => state.currentPageShellOnly,
    isCurrentPreviewMode: (state): boolean => state.currentPreview.isActive,
    isCurrentPageNotFound: (state): boolean => state.currentPageNotFound,
  },

  actions: {
    // --- Public API (entry points) ---

    async initialize() {
      this.status = 'loading';
      this.error = null;
      this.siteBootstrapError = null;
      let hasReadyState = false;

      try {
        const siteEtag = await this.internalHydrateFromCache();
        this.internalMarkKnownPagesAsIdle();
        hasReadyState = await this.internalTryReadyFromCachedRootDetail();
        try {
          await this.internalRefreshListingFromApi(siteEtag);
        } catch {
          // Offline or listing fetch failed: keep hydrated listing from IndexedDB when present.
          if (!this.data?.items?.length) {
            const fallback = await getListingFromPageTable();
            if (fallback?.items?.length) {
              this.data = normalizePagesApiResponse(fallback);
              this.internalMarkKnownPagesAsIdle();
            }
          }
        }
        hasReadyState = await this.internalFinalizeReadyAfterListingRefresh(hasReadyState);
        if (hasReadyState) {
          void this.internalPrefetchPageDetails();
        }
      } catch (e) {
        if (!hasReadyState) {
          this.status = 'error';
          this.siteBootstrapError = null;
          this.error = e instanceof Error ? e.message : String(e);
        }
      }
    },

    async syncCurrentRoute(path: string, query: RouteQueryLike): Promise<void> {
      const requestKey = ++this.currentRouteRequestKey;
      const { contentType, token } = parsePreviewQuery(query);

      if (contentType && token) {
        await this.internalSyncPreviewRoute(requestKey, contentType, token);
        return;
      }

      await this.internalSyncPublishedRoute(path, requestKey);
    },

    /**
     * Get full page detail by id.
     * Uses cached detail when it matches listing freshness metadata and only
     * requests from the API when detail is missing or known stale.
     * Returns null on 404 or fetch error.
     */
    async getPageDetail(pageId: number): Promise<PageDetailRow | null> {
      const fromMemory = this.internalGetPageDetailFromMemory(pageId);
      if (fromMemory !== undefined) return fromMemory;

      const fromCache = await this.internalGetPageDetailFromCache(pageId);
      if (fromCache !== undefined) return fromCache;

      this.internalSetPageDetailLoading(pageId, true);
      const result = await fetchPageDetailFromApi(pageId, undefined);
      if (result !== null) {
        this.internalApplyPageDetail(pageId, result);
      } else {
        this.internalApplyPageDetail(pageId, null);
      }
      this.internalSetPageDetailLoading(pageId, false);
      return this.pageDetailsById[pageId] ?? null;
    },

    /**
     * Revalidate site listing in the background (e.g. on navigation). Fetches with
     * If-None-Match; on 200 updates store and idb so new pages appear. No-op if
     * store is not ready or idb unavailable.
     */
    async revalidateSite(): Promise<void> {
      if (this.status !== 'ready' || !this.data) return;
      let etag: string | null = null;
      try {
        const row = await contentDb.site.get('default');
        etag = row?.etag ?? null;
      } catch {
        return;
      }
      const headers: HeadersInit = {};
      if (shouldSendHttpCacheValidators() && etag && !needsContractListingRefresh()) {
        headers['If-None-Match'] = etag;
      }
      try {
        const response = await fetch(PAGES_URL, { credentials: 'same-origin', headers });
        if (response.status === 304) return;
        if (!response.ok) return;
        const data = (await response.json()) as PagesApiResponse;
        this.data = normalizePagesApiResponse(data);
        this.internalMarkKnownPagesAsIdle();
        const staleIds = this.internalReconcileInMemoryDetailsWithListing(
          listingItemsExpandedForNav(this.data.items)
        );
        try {
          const newEtag = response.headers.get('ETag') ?? null;
          await applyListingResponse(data, newEtag);
        } catch {
          // Ignore cache write failures
        }
        await this.internalRefreshStaleDetails(staleIds);
        markContractGenerationApplied();
      } catch {
        // Ignore revalidation errors; keep showing current data
      }
    },

    getKnownLocales(): string[] {
      const expanded = listingItemsExpandedForNav(this.data?.items);
      if (!expanded.length) return [];
      return [...new Set(expanded.map((item) => item.meta.locale))];
    },

    // --- Internal helpers (implementation detail) ---

    internalSetDetailState(pageId: number, state: PageDetailState): void {
      this.detailStateById[pageId] = state;
    },

    internalSetTypedDetail(pageId: number, row: PageDetailRow): void {
      this.pageDetailsById[pageId] = row;
      this.internalSetDetailState(pageId, 'ready');
    },

    internalReconcileInMemoryDetailsWithListing(items: PageItem[]): number[] {
      const staleIds: number[] = [];
      for (const item of items) {
        const existing = this.pageDetailsById[item.id];
        if (!existing) continue;
        const existingMeta =
          existing.meta && typeof existing.meta === 'object'
            ? (existing.meta as unknown as Record<string, unknown>)
            : {};
        const merged: PageDetailRow = {
          ...existing,
          id: item.id,
          title: existing.title ?? item.title,
          meta: {
            ...existingMeta,
            ...item.meta,
          },
        };
        this.pageDetailsById[item.id] = merged;
        if (!isDetailRowFullyFresh(merged)) {
          staleIds.push(item.id);
        }
      }
      return staleIds;
    },

    async internalRefreshStaleDetails(pageIds: number[]): Promise<void> {
      if (pageIds.length === 0) return;
      const staleIds = Array.from(new Set(pageIds));
      const DETAIL_REFRESH_CONCURRENCY = 4;
      await runWithConcurrency(staleIds, DETAIL_REFRESH_CONCURRENCY, async (pageId) => {
        if (this.loadingDetailIds[pageId]) return;
        const staleRow = this.pageDetailsById[pageId];
        if (!staleRow) return;
        this.loadingDetailIds[pageId] = true;
        try {
          const result = await fetchPageDetailFromApi(pageId, staleRow);
          if (result !== null) {
            this.internalSetTypedDetail(pageId, result);
          }
        } finally {
          delete this.loadingDetailIds[pageId];
        }
      });
    },

    internalMarkKnownPagesAsIdle(): void {
      const items = listingItemsExpandedForNav(this.data?.items);
      if (!items.length) return;
      for (const item of items) {
        if (this.detailStateById[item.id] === undefined) {
          this.detailStateById[item.id] = 'idle';
        }
      }
    },

    isTypedDetailRow(row: PageDetailRow | null | undefined): row is PageDetailRow {
      return !!row && row.detailDate != null;
    },

    internalApplyPageDetail(pageId: number, row: PageDetailRow | null): void {
      this.pageDetailsById[pageId] = row;
      this.internalSetDetailState(pageId, row === null ? 'missing' : 'ready');
    },

    internalSetPageDetailLoading(pageId: number, loading: boolean): void {
      if (loading) {
        this.loadingDetailIds[pageId] = true;
        this.internalSetDetailState(pageId, 'loading');
      } else {
        delete this.loadingDetailIds[pageId];
      }
    },

    internalRevalidatePageDetailInBackground(pageId: number, staleRow: PageDetailRow): void {
      if (this.loadingDetailIds[pageId]) return;
      this.loadingDetailIds[pageId] = true;
      void fetchPageDetailFromApi(pageId, staleRow)
        .then((result) => {
          if (result !== null) {
            this.internalApplyPageDetail(pageId, result);
          }
        })
        .finally(() => {
          delete this.loadingDetailIds[pageId];
        });
    },

    internalGetPageDetailFromMemory(pageId: number): PageDetailRow | null | undefined {
      const existing = this.pageDetailsById[pageId];
      if (!existing) return undefined;
      if (isDetailRowFullyFresh(existing)) {
        this.internalSetDetailState(pageId, 'ready');
        return existing;
      }
      this.internalSetDetailState(pageId, 'ready');
      this.internalRevalidatePageDetailInBackground(pageId, existing);
      return existing;
    },

    async internalGetPageDetailFromCache(pageId: number): Promise<PageDetailRow | null | undefined> {
      let row;
      try {
        row = await findConsolidatedRowByPageId(pageId);
      } catch {
        return undefined;
      }
      if (!row) return undefined;
      const cached = extractDetailSliceFromConsolidated(row, pageId);
      if (!cached) return undefined;
      if (isDetailRowFullyFresh(cached)) {
        this.internalApplyPageDetail(pageId, cached);
        return cached;
      }
      this.internalApplyPageDetail(pageId, cached);
      this.internalRevalidatePageDetailInBackground(pageId, cached);
      return cached;
    },

    async internalHydrateFromCache(): Promise<string | null> {
      let siteEtag: string | null = null;
      try {
        const siteRow = await contentDb.site.get('default');
        siteEtag = siteRow?.etag ?? null;
        const listingFromCache = await getListingFromPageTable();
        if (listingFromCache && listingFromCache.items.length > 0) {
          this.data = normalizePagesApiResponse(listingFromCache);
          this.internalMarkKnownPagesAsIdle();
        }
      } catch {
        // IndexedDB unavailable (e.g. private browsing); continue to fetch
      }
      return siteEtag;
    },

    async internalTryReadyFromCachedRootDetail(): Promise<boolean> {
      const cachedRootId = this.internalGetDefaultRootId();
      if (cachedRootId === null) return false;
      const hasRootDetail = await this.internalEnsureRootDetailAvailable(cachedRootId);
      if (!hasRootDetail) return false;
      if (!this.isTypedDetailReady(cachedRootId)) return false;
      this.internalSetReadyWithNavigation(cachedRootId);
      return true;
    },

    internalGetDefaultRootId(): number | null {
      return this.internalGetHomePageIdForLocale(DEFAULT_LOCALE);
    },

    internalGetHomePageIdForLocale(locale: string): number | null {
      const items = this.data?.items;
      if (!items?.length) return null;
      const homePage = items.find((item) => {
        const meta = item.meta as { type?: string; locale?: string } | undefined;
        return meta?.type === 'home.HomePage' && meta?.locale === locale;
      });
      return homePage?.id ?? null;
    },

    async internalEnsureRootDetailAvailable(rootId: number): Promise<boolean> {
      const inState = this.pageDetailsById[rootId];
      if (this.isTypedDetailRow(inState)) {
        this.internalSetTypedDetail(rootId, inState);
        if (!isDetailRowFullyFresh(inState)) {
          this.internalRevalidatePageDetailInBackground(rootId, inState);
        }
        return true;
      }

      let cached: PageDetailRow | undefined;
      try {
        const row = await findConsolidatedRowByPageId(rootId);
        cached = row ? extractDetailSliceFromConsolidated(row, rootId) ?? undefined : undefined;
      } catch {
        // IndexedDB unavailable; continue with API fetch
      }

      if (this.isTypedDetailRow(cached)) {
        this.internalSetTypedDetail(rootId, cached);
        if (!isDetailRowFullyFresh(cached)) {
          this.internalRevalidatePageDetailInBackground(rootId, cached);
        }
        return true;
      }

      this.internalSetDetailState(rootId, 'loading');
      const fetched = await fetchPageDetailFromApi(rootId, cached);
      if (fetched !== null) {
        this.internalSetTypedDetail(rootId, fetched);
        return true;
      }
      this.internalSetDetailState(rootId, 'missing');
      this.pageDetailsById[rootId] = null;
      return false;
    },

    internalResetPreviewState(): void {
      this.currentPreview.isActive = false;
      this.currentPreview.contentType = null;
      this.currentPreview.token = null;
      this.currentPreview.loading = false;
      this.currentPreview.error = false;
      this.currentPreview.payload = undefined;
    },

    internalResetPublishedRouteState(): void {
      this.currentPageId = null;
      this.currentPageLoading = false;
      this.currentPageError = null;
      this.currentPageShellOnly = false;
      this.currentPageNotFound = false;
    },

    async internalSyncPreviewRoute(requestKey: number, contentType: string, token: string): Promise<void> {
      this.currentPreview.isActive = true;
      this.currentPreview.contentType = contentType;
      this.currentPreview.token = token;
      this.currentPreview.loading = true;
      this.currentPreview.error = false;
      this.currentPreview.payload = undefined;
      this.internalResetPublishedRouteState();

      const previewPayload = await fetchPagePreview(contentType, token);
      if (requestKey !== this.currentRouteRequestKey) return;
      this.currentPreview.payload = previewPayload;
      this.currentPreview.loading = false;
      this.currentPreview.error = previewPayload === null;
    },

    async internalSyncPublishedRoute(path: string, requestKey: number): Promise<void> {
      this.internalResetPreviewState();

      const items = listingItemsExpandedForNav(this.data?.items);
      const page = resolvePageFromPathname(items, path);
      this.currentPageId = page?.id ?? null;
      this.currentPageError = null;
      this.currentPageNotFound = page === null && items.length > 0;
      if (!page) {
        this.currentPageLoading = false;
        return;
      }

      this.currentPageLoading = true;
      const detail = await this.getPageDetail(page.id);
      if (requestKey !== this.currentRouteRequestKey) return;
      this.currentPageLoading = false;
      if (detail === null) {
        const offline = typeof navigator !== 'undefined' && !navigator.onLine;
        if (offline) {
          this.currentPageShellOnly = true;
          this.currentPageError = null;
        } else {
          this.currentPageShellOnly = false;
          this.currentPageError = 'Could not load page.';
        }
      } else {
        this.currentPageShellOnly = false;
      }
    },

    async internalFetchListingWithEtag(siteEtag: string | null): Promise<Response> {
      await devFetchDelay(1000);
      const headers: HeadersInit = {};
      if (shouldSendHttpCacheValidators() && siteEtag && !needsContractListingRefresh()) {
        headers['If-None-Match'] = siteEtag;
      }
      return fetch(PAGES_URL, { credentials: 'same-origin', headers });
    },



    async internalRefreshRootDetailFromApi(rootId: number): Promise<boolean> {
      let cached: PageDetailRow | undefined;
      try {
        const row = await findConsolidatedRowByPageId(rootId);
        cached = row ? extractDetailSliceFromConsolidated(row, rootId) ?? undefined : undefined;
      } catch {
        // IndexedDB unavailable; continue with network call
      }

      if (cached && isDetailRowFullyFresh(cached)) {
        this.internalSetTypedDetail(rootId, cached);
        return true;
      }

      this.internalSetDetailState(rootId, 'loading');
      const result = await fetchPageDetailFromApi(rootId, cached);
      if (result !== null) {
        this.internalSetTypedDetail(rootId, result);
        return true;
      }
      if (this.isTypedDetailRow(cached)) {
        this.internalSetTypedDetail(rootId, cached);
        if (!isDetailRowFullyFresh(cached)) {
          this.internalRevalidatePageDetailInBackground(rootId, cached);
        }
        return true;
      }
      this.internalSetDetailState(rootId, 'missing');
      if (!cached) this.pageDetailsById[rootId] = null;
      return false;
    },

    internalSetReadyWithNavigation(rootId: number): void {
      if (!this.isTypedDetailReady(rootId)) return;
      this.status = 'ready';
      this.error = null;
    },

    async internalRefreshListingFromApi(siteEtag: string | null): Promise<ListingRefreshOutcome> {
      const response = await this.internalFetchListingWithEtag(siteEtag);
      if (response.status === 304) return 'notModified';
      if (!response.ok) {
        throw new Error(`CMS API error: ${response.status} ${response.statusText}`);
      }
      const data = (await response.json()) as PagesApiResponse;
      this.data = normalizePagesApiResponse(data);
      this.internalMarkKnownPagesAsIdle();
      const staleIds = this.internalReconcileInMemoryDetailsWithListing(
        listingItemsExpandedForNav(this.data.items)
      );
      try {
        const etag = response.headers.get('ETag') ?? null;
        await applyListingResponse(data, etag);
      } catch {
        // Ignore cache write failures
      }
      await this.internalRefreshStaleDetails(staleIds);
      markContractGenerationApplied();
      return 'updated';
    },

    async internalFinalizeReadyAfterListingRefresh(hasReadyState: boolean): Promise<boolean> {
      const rootId = this.internalGetDefaultRootId();
      if (rootId === null) {
        if (!hasReadyState) {
          this.status = 'error';
          this.siteBootstrapError = 'no_site_root';
          this.error = null;
        }
        return hasReadyState;
      }

      const rootReady = await this.internalRefreshRootDetailFromApi(rootId);
      if (!rootReady) {
        if (!hasReadyState) {
          this.status = 'error';
          this.siteBootstrapError = 'root_detail_failed';
          this.error = null;
        }
        return hasReadyState;
      }

      this.internalSetReadyWithNavigation(rootId);
      return true;
    },

    /**
     * Prefetch full details for all listed pages in the background. Does not set
     * loadingDetailIds; only updates IndexedDB and pageDetailsById on success. Best-effort.
     */
    internalPrefetchPageDetails(): void {
      if (this.status !== 'ready' || !this.data) return;
      const ids = this.data.items.map((item) => item.id)
        .filter((id) => {
          const row = this.pageDetailsById[id];
          return !(row && isDetailRowFullyFresh(row));
        });
      if (ids.length === 0) return;

      const PREFETCH_CONCURRENCY = 4;
      void runWithConcurrency(ids, PREFETCH_CONCURRENCY, async (pageId) => {
        let cached: PageDetailRow | undefined;
        try {
          const crow = await findConsolidatedRowByPageId(pageId);
          cached = crow ? extractDetailSliceFromConsolidated(crow, pageId) ?? undefined : undefined;
        } catch {
          // IndexedDB unavailable
        }

        if (cached && isDetailRowFullyFresh(cached)) {
          this.internalSetTypedDetail(pageId, cached);
          return;
        }

        const result = await fetchPageDetailFromApi(pageId, cached);
        if (result !== null) {
          this.internalSetTypedDetail(pageId, result);
        } else if (this.isTypedDetailRow(cached)) {
          this.internalSetTypedDetail(pageId, cached);
          if (!isDetailRowFullyFresh(cached)) {
            this.internalRevalidatePageDetailInBackground(pageId, cached);
          }
        } else if (cached === undefined) {
          this.internalSetDetailState(pageId, 'missing');
          this.pageDetailsById[pageId] = null;
        } else {
          this.internalSetDetailState(pageId, 'error');
        }
      });
    },
  },
});

/** Run promises with a concurrency limit (at most `limit` in flight). */
async function runWithConcurrency<T>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<void>
): Promise<void> {
  let index = 0;
  async function next(): Promise<void> {
    if (index >= items.length) return;
    const i = index++;
    const item = items[i];
    if (item === undefined) return;
    await fn(item);
    await next();
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, next));
}
